import {
  Component,
  OnInit,
  Input,
  SimpleChanges,
  OnChanges,
  ViewEncapsulation,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { isEmpty, round } from 'lodash';
import { CollectorApiService, ControllerApiService } from '@nrwl/test-setup';
import {
  HelpersService,
  LmapService,
  TestMode,
  WindowWalkService,
  LicensesPageService,
  DevicesService,
  TestModeLabel
} from '@nrwl/common-lib';

import {
  ReportsTransformationService,
  TableData
} from '../../../services/reports.transformation.service';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const diagnosisMap = require('../diagnosis/report/diagnosis-map.json');

@Component({
  selector: 'nrwl-ftp-final-reports',
  templateUrl: './ftp-final-reports.component.html',
  styleUrls: ['./ftp-final-reports.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FtpFinalReportsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('pdfPage') element: ElementRef;
  @Input() reportStartTime: string;
  @Input() scheduleId: number;
  @Input() runId: number;
  @Input() testName: string;
  @Input() onDemandTest: boolean;
  loading: boolean;
  requestInfo = {};
  upstreamSummary = {};
  downstreamSummary = {};
  testSummary = {};
  rtRequestCtr: number;
  rtTotalTime: number;
  windowResults = [];
  windowSummary = [];
  configuration = {};
  //eslint-disable-next-line
  overallConfiguration: any;
  rtResultsRequested: number; // get all at once...max expected is < 8,000
  currentUrl: string;
  logoUrl: string;
  message: string;
  isThinClientTest: boolean;
  reportTitle: string;
  testType: string;
  deviceType: string;
  upstreamDiagnosisMap = {};
  downstreamDiagnosisMap = {};
  diagnosisMessage = {};
  tagging = 'UNTAGGED';
  l1Rate;
  l3Rate;
  l2Rate;
  finishStatus: string;
  startRTRequestTime: Date;
  upstreamWindowResult;
  downstreamWindowResult;
  displayedColumns: string[] = ['header', 'value'];
  testConfigsDataSource;
  testResultsDataSource;
  bulletChartOptions = {};
  runSaturationWindow = false;
  ftpTestResults: Array<TableData>;
  dynamicSaturationWindow = false;
  shaId: string;
  selectedOriginator: any;
  licenseDetails: any = [];
  throughputInnerTableHeader = `<tr>
  <td class="throughput-inner-table-data">L1</td>
  <td class="throughput-inner-table-data">L2</td>
  <td class="throughput-inner-table-data">L3</td>
  <td class="throughput-inner-table-data">L4</td></tr>`;
  dataAvailable = false;
  private readonly unsubscribe: Subject<void> = new Subject();
  serverConfig: any;
  reportmessage: any;
  startDate = null;
  stopDate = null;
  duration: string;
  overallVerdict: string;
  constructor(
    private readonly helperService: HelpersService,
    private windowWalkService: WindowWalkService,
    private lmapService: LmapService,
    private transformService: ReportsTransformationService,
    private collectorApiService: CollectorApiService,
    private controllerApiService: ControllerApiService,
    private licenseService: LicensesPageService,
    readonly deviceService: DevicesService
  ) {
    this.loading = false;
    this.rtRequestCtr = 0;
    this.rtTotalTime = 0;
    this.rtResultsRequested = 10000; // get all at once...max expected is < 8,000
    this.currentUrl = window.location.href;
    this.logoUrl = 'images/viavi-logo-purple.png';
    this.isThinClientTest = false;
    this.reportTitle = 'Ftp Test Report';
    this.testType = null;
    this.deviceType = null;
  }

  ngOnInit(): void {
    this.init();
  }
  init(): void {
    this.loading = true;
    const getDiagnosis = diagnosisMap;
    this.diagnosisMessage = getDiagnosis.data;
    this.deviceType = this.windowWalkService.getDeviceType();
    this.upstreamDiagnosisMap = this.createDiagnosisMap('Upload');
    this.downstreamDiagnosisMap = this.createDiagnosisMap('Download');
    this.getLicenseDetails();
    this.getServerConfig();
    this.getTestConfig();
  }

  getServerConfig(): void {
    this.controllerApiService.getServerConfig().subscribe(response => {
      this.serverConfig = response.data;
      const serverConfig = response.data['reports'];
      if (serverConfig && serverConfig.logo && serverConfig.logo.url) {
        this.logoUrl = serverConfig.logo.url;
      }
      if (serverConfig && serverConfig.message) {
        this.reportmessage = serverConfig.message;
      }
    });
  }

  getRTResults(): void {
    this.rtRequestCtr++;
    this.startRTRequestTime = new Date();
    const maxResults = this.onDemandTest ? 1000 : 180;
    this.collectorApiService
      .getQueryReportByFromTime(this.scheduleId, this.runId, 'rt', this.reportStartTime, maxResults)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(res => {
        if (res.status === 'success') {
          const getRTResultResponse = res.data.filter(data => {
            const instanceStartTime = this.lmapService.getTagValue(
              data['ietf-lmap-report:report'].result[0].tag,
              'instance-start-time'
            );
            return instanceStartTime === this.reportStartTime;
          });
          this.processRTResults(getRTResultResponse);
          this.calculateTotalStat();
        }
      });
  }
  getLicenseDetails(): void {
    this.licenseService.getLicenseCount().subscribe(response => {
      this.licenseDetails = response;
    });
  }

  //eslint-disable-next-line
  processRTResults(response) {
    // Check response to make sure it is valid
    const errorMsg = this.helperService.getResponseErrorMsg(response);
    if (errorMsg == null) {
      if (response) {
        let totalRtReports = 0;
        let lastSampleDate = '';
        if (response.length && response.length > 0) {
          totalRtReports = response.length;
          lastSampleDate = this.lmapService.getReportPostDate(response, response.length - 1);
        }

        // separate the report content into results per window
        const allRtReport = response;
        for (let i = 0; i < allRtReport.length; i++) {
          const report = this.lmapService.ltranslateLmapReportIndexToAngular(allRtReport, null, i);
          if (report[0] && report[0].status.indexOf('ABORTED') !== -1) {
            this.finishStatus = 'Test Aborted';
          }

          if (report[0] && report[0].status.indexOf('COMPLETE') !== -1) {
            this.finishStatus = 'Test Complete';
          }

          if (
            report &&
            report[0] &&
            report[0].status &&
            report[0].status.indexOf('WINDOW') !== -1
          ) {
            let thisWindow = null;
            for (let x = 0; x < this.windowResults.length; x++) {
              if (this.windowResults[x] && this.windowResults[x].status === report[0].status) {
                thisWindow = this.windowResults[x];
              }
            }
            if (thisWindow === null) {
              thisWindow = {
                status: report[0].status,
                id: report[0]['window-id'] || 1,
                seq: [], //track which seq number the data came from to prevent duplicates
                thruput: [],
                rtt: [],
                transmitted: [],
                retransmitted: [],
                retransRatio: []
              };
              // put a starter value in each array since High Charts does not display the first value
              thisWindow.thruput.push(0);
              thisWindow.rtt.push(0);
              thisWindow.transmitted.push(0);
              thisWindow.retransmitted.push(0);
              thisWindow.retransRatio.push(0);

              this.windowResults.push(thisWindow);
            }

            if (report[0]['current-thruput'] !== '') {
              let exists = false;
              for (let z = 0; z < thisWindow.seq.length; z++) {
                if (thisWindow.seq[z] === report[0][this.lmapService.CONST.resultSequenceId]) {
                  exists = true;
                  break;
                }
              }
              if (exists === false) {
                const transmitted = round(parseFloat(report[0]['current-transmitted-frames']), 3);
                const retransmitted = round(
                  parseFloat(report[0]['current-retransmitted-frames']),
                  3
                );

                thisWindow.id = report[0]['window-id'] || 1; // sometimes empty in initial report so always set it
                thisWindow.seq.push(report[0][this.lmapService.CONST.resultSequenceId]);
                thisWindow.thruput.push(round(parseFloat(report[0]['current-thruput']), 3));
                thisWindow.rtt.push(round(parseFloat(report[0]['current-rtt']), 3));
                thisWindow.transmitted.push(transmitted);
                thisWindow.retransmitted.push(retransmitted);
                const retransmittedRatio = (retransmitted / transmitted) * 100;
                if (transmitted !== 0) {
                  thisWindow.retransRatio.push(round(retransmittedRatio, 3));
                } else {
                  thisWindow.retransRatio.push(0);
                }
              }
            }
          }
        }

        if (!this.finishStatus) {
          this.finishStatus = 'Test Aborted';
        }

        const currentTime = new Date();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const elapsedTime = currentTime - this.startRTRequestTime;
        this.rtTotalTime += elapsedTime;
        if (this.rtResultsRequested === totalRtReports) {
          setTimeout(function () {
            this.getRTResults(lastSampleDate);
          }, 500);
        } else {
          // order windowResults so that Window B's results follow Window A's for the same Window test
          let notDone = true;
          while (notDone === true) {
            notDone = false;
            for (let x = 0; x < this.windowResults.length && notDone === false; x = x + 2) {
              if (x + 1 < this.windowResults.length) {
                if (this.windowResults[x]?.status?.indexOf('ENGINE_A') >= 0) {
                  const engineAStatus = this.windowResults[x].status;
                  const engineBStatus = engineAStatus.replace('ENGINE_A', 'ENGINE_B');
                  if (this.windowResults[x + 1].status !== engineBStatus) {
                    // find matching B data and move it after A
                    for (let z = 0; z < this.windowResults.length; z++) {
                      if (this.windowResults[z].status === engineBStatus) {
                        // first remove the item that is later in the array
                        const engineBData = this.windowResults.splice(z, 1);
                        if (engineBData[0]) {
                          // insert it righ after the associated A data
                          this.windowResults.splice(x + 1, 0, engineBData[0]);
                        }
                        //once we have changed the array, start checking from the beginning
                        notDone = true;
                        break;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    } else {
      this.windowResults = [];
    }
    this.dataAvailable = true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getMatchedWindowResult(windowId: string): any {
    return this.windowResults.find(result => result['id'] === windowId);
  }

  getTestConfig(): void {
    this.controllerApiService
      .getTestConfig(this.scheduleId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        if (response.status === 'success') {
          this.overallConfiguration = response.data.config;
          this.shaId = response.data?.sha ? response.data.sha : null;
          this.processReport();
          this.getRTResults();
        }
      });
  }

  //eslint-disable-next-line
  getFinalReportBasedOnStartDate(res: any): any {
    if (res.data && res.data.length > 0) {
      if (
        res.data[0]?.['ietf-lmap-report:report'] &&
        res.data[0]?.['ietf-lmap-report:report']?.result
      ) {
        const startDate = res.data[0]['ietf-lmap-report:report'].result[0].start;
        return res.data.filter(
          item => item['ietf-lmap-report:report'].result[0].start === startDate
        );
      }
    }
    return res.data;
  }

  // ToDo return type and parameter can be typed while integrated with backend.
  // eslint-disable-next-line
  processReport(): any {
    this.collectorApiService
      .getQueryReportByFromTime(this.scheduleId, this.runId, 'final', this.reportStartTime, 1000)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(res => {
        if (res.status === 'success') {
          const getReportResponse = this.getFinalReportBasedOnStartDate(res);
          const latestReport = this.windowWalkService.getLatestReport(getReportResponse);
          // Set start/stop dates
          this.startDate = null;
          this.stopDate = null;

          if (getReportResponse.length) {
            for (const reportData of getReportResponse) {
              const report = reportData['ietf-lmap-report:report'];
              if (report) {
                const myStopDate = new Date(report.date);
                if (!this.stopDate || myStopDate.getTime() > this.stopDate.getTime()) {
                  this.stopDate = myStopDate;
                }

                if (!this.startDate && report.result?.length > 0) {
                  this.startDate = new Date(report.result[0].start);
                }
              }
            }
          }
          this.processStartStopDates();

          //overriding test config with current testid config
          const currentRunConfig = this.getTestConfigOption(res.data);
          if (!isEmpty(currentRunConfig)) {
            this.overallConfiguration.config[0].primaryConfig[
              'ietf-lmap-control:lmap'
            ].schedules.schedule[0].action[0].option = currentRunConfig;
          }

          this.configuration = this.parseConfiguration({
            config: this.overallConfiguration
          });
          this.selectedOriginator = this.deviceService.findById(
            this.configuration?.['engineA']?.address
          );
          this.runSaturationWindow =
            this.configuration?.['runSaturationWindow'] === 'true' ? true : false;
          this.dynamicSaturationWindow =
            this.configuration?.['dynamicSaturation'] === 'true' ? true : false;
          const testConfigs = this.transformService.getHTTPTestConfigs(
            this.configuration,
            this.testType
          );
          this.testConfigsDataSource = new MatTableDataSource(testConfigs);
          this.requestInfo = this.createRequestInfo(this.overallConfiguration);

          this.processResults(latestReport, this.configuration);
        }
      });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  processStartStopDates() {
    function padZero(x) {
      return x < 10 ? '0' + x : x.toString();
    }
    if (this.stopDate && this.startDate) {
      let diffTime = this.stopDate.getTime() - this.startDate.getTime();
      diffTime = diffTime / 1000;

      let minutes = Math.floor(diffTime / 60);
      const seconds = diffTime % 60;
      let hours = 0;
      if (minutes >= 60) {
        hours = Math.floor(minutes / 60);
        minutes = minutes % 60;
      }
      let days = 0;
      if (hours >= 24) {
        days = Math.floor(hours / 24);
        hours = hours % 24;
      }

      this.duration =
        (days ? days + 'd ' : '') +
        (hours ? hours + 'h ' : '') +
        (minutes ? minutes + 'm ' : '') +
        seconds +
        's';
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  parseConfiguration(data: any): any {
    const configuration = {};

    if (data.config) {
      const config = this.windowWalkService.getTestOptions(
        'ftp',
        ['ftp', 'hide-client-results'],
        data.config
      );
      if (config && config['ftp']) {
        const testOptions = JSON.parse(config['ftp'])['viavi-solutions-ftp:ftp'];
        if (testOptions) {
          if (testOptions['ftp-parameters']) {
            const parms = testOptions['ftp-parameters'];
            for (const x in parms) {
              switch (x) {
                case 'mode':
                  configuration['mode'] = parms[x];
                  break;
                case 'direction':
                  configuration['direction'] = parms[x];
                  break;
                case 'type':
                  configuration['type'] = parms[x];
                  break;
                case 'bdp-table-version':
                  configuration['bdpTableVersion'] = parseInt(parms[x]);
                  break;
                case 'threshold-enabled':
                  //Force to string, because MTS is sending string
                  configuration['thresholdnEabled'] = parms[x]; // parms[x].toString();
                  break;
                case 'engine-a-to-b-threshold':
                  configuration['engineAtoBThreshold'] = parseInt(parms[x]); //parms[x].toString();
                  break;
                case 'engine-b-to-a-threshold':
                  configuration['engineBtoAThreshold'] = parseInt(parms[x]); //parms[x] === 0 ? 15 : parms[x];
                  break;
                case 'max-test-duration':
                  configuration['maxTestDuration'] = parms[x];
                  break;
                // case 'tos-dscp':
                //   configuration['serviceType'] = parms[x];
                //   if (configuration['serviceType'].type === 'tos') {
                //     this.windowWalkService.formatTos(configuration);
                //   } else {
                //     configuration['serviceType'].dscp = this.helperService.formatDiffValue(
                //       configuration['serviceType'].dscp
                //     );
                //   }

                //   break;
                case 'tcp-port':
                  if (parseInt(parms[x]) === 0) {
                    configuration['tcpPort'] = 'AUTO';
                  } else {
                    configuration['tcpPort'] = parseInt(parms[x]);
                  }
                  break;
                case 'engine-a-to-b-url':
                  configuration['engineAtoBUrl'] = parms[x];
                  break;
                case 'engine-b-to-a-url':
                  configuration['engineBtoAUrl'] = parms[x];
                  break;
                case 'enable-rtt':
                  configuration['enableRtt'] = parseInt(parms[x]);
                  break;
                default:
                  break;
              }
            }
          }

          configuration['engineA'] = testOptions['engine-a-info'];
          configuration['engineB'] = testOptions['engine-b-info'];
        }
      }

      if (config && config['hide-client-results']) {
        configuration['hideClientResults'] = config['hide-client-results'];
      }
    }

    return configuration;
  }

  //eslint-disable-next-line
  getTestConfigOption(response): any {
    let optionValue = [];
    if (response.length) {
      const reports = response[0];
      const testOptions = reports['ietf-lmap-report:report'].result[0].option;
      optionValue = testOptions;
    }
    return optionValue;
  }

  //eslint-disable-next-line
  createRequestInfo(data: any): any {
    const requestInfo = {};
    requestInfo['testName'] =
      data?.config[0]?.primaryConfig['ietf-lmap-control:lmap']?.schedules?.schedule[0]?.name;
    requestInfo['mode'] =
      this.configuration?.['mode'] === TestMode.MA_TO_SERVER
        ? TestModeLabel.MA_TO_SERVER
        : this.configuration?.['mode'] === TestMode.MA_TO_EXTERNAL
        ? TestModeLabel.MA_TO_EXTERNAL
        : TestModeLabel.MA_TO_SERVER;
    if (data && data.test && data.test['extended-config']) {
      requestInfo['techInfo'] = data.test['extended-config'].technicianInfo;
      requestInfo['testName'] = data.test['extended-config'].testInfo.name;
      requestInfo['mode'] =
        data.test['extended-config'].test.mode === TestMode.MA_TO_SERVER
          ? TestModeLabel.MA_TO_SERVER
          : data.test['extended-config'].test.mode === TestMode.MA_TO_EXTERNAL
          ? TestModeLabel.MA_TO_EXTERNAL
          : TestModeLabel.MA_TO_SERVER;
      requestInfo['comments'] = data.test['extended-config'].testInfo.comments;
    }

    if (data && data.test && data.test['test-list-data']) {
      requestInfo['customerInfo'] = data.test['test-list-data'].customer;
      requestInfo['mode'] =
        data.test['test-list-data'].mode === TestMode.MA_TO_SERVER
          ? TestModeLabel.MA_TO_SERVER
          : data.test['extended-config'].test.mode === TestMode.MA_TO_EXTERNAL
          ? TestModeLabel.MA_TO_EXTERNAL
          : TestModeLabel.MA_TO_SERVER;
    }
    let agentName = data && data && data.agent && data.agent.primary ? data.agent.primary.id : null;
    if (!agentName) {
      agentName =
        data &&
        data &&
        data.test &&
        data.test['extended-config'] &&
        data.test['extended-config'].testPoints &&
        data.test['extended-config'].testPoints > 0
          ? data.test['extended-config'].testPoints[0].name
          : null;
    }
    return requestInfo;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getWindowResults(result, configuration): any {
    let windowResults = {};
    const upstreamIdeal = [];
    const upstreamAvg = [];
    const upstreamWindow = [];
    const downstreamIdeal = [];
    const downstreamAvg = [];
    const downstreamWindow = [];
    let windowResultsReceived = 0;
    const table = result['ietf-lmap-report:report'].result[0].table[0];

    // Column indices
    const originatorIndex = table.column.indexOf('originator');
    const idealThruIndex = table.column.indexOf('ideal-thruput');
    const avgThruIndex = table.column.indexOf('avg-thruput');
    const windowIdIndex = table.column.indexOf('window-id');
    const windowSizeIndex = table.column.indexOf('window-size');
    const connectionIndex = table.column.indexOf('connections');
    const mssIndex = table.column.indexOf('mss');
    const mtuIndex = table.column.indexOf('mtu');
    const rttIndex = table.column.indexOf('baseline-rtt');
    const rttBaseIndex = table.column.indexOf('baseline-rtt');
    const engAIpIndex = table.column.indexOf('engine-a-ip');
    const engBPubIPIndex = table.column.indexOf('engine-b-public-ip');
    const engBPriIPIndex = table.column.indexOf('engine-b-private-ip');
    const tcpPortIndex = table.column.indexOf('tcp-port');

    // Initialize windows
    for (let i = 1; i <= configuration.numWindow; i++) {
      upstreamWindow.push(`Window ${i}`);
      upstreamIdeal.push(0);
      upstreamAvg.push(0);

      downstreamWindow.push(`Window ${i}`);
      downstreamIdeal.push(0);
      downstreamAvg.push(0);
    }

    // Populate upstream/downstream throughput
    const values = table.row;
    windowResultsReceived = values.length;

    for (let i = 0; i < values.length; i++) {
      const row = values[i].value;

      // Skip if critical fields are empty
      if (!row[windowIdIndex] || !row[idealThruIndex]) {
        continue;
      }

      const originator = row[originatorIndex];
      let windowId = parseInt(row[windowIdIndex], 10);
      const idealValue = parseFloat(row[idealThruIndex]) || 0;
      const avgValue = parseFloat(row[avgThruIndex]) || 0;

      let ideal = upstreamIdeal;
      let avg = upstreamAvg;
      let window = upstreamWindow;

      if (originator === 'engine-b') {
        ideal = downstreamIdeal;
        avg = downstreamAvg;
        window = downstreamWindow;

        if (configuration.numWindow) {
          windowId -= configuration.numWindow;
        }
        if (configuration.runSaturationWindow === 'true') {
          windowId -= 1;
        }
      }

      // Avoid negative or invalid indices
      if (windowId < 0 || windowId >= configuration.numWindow) {
        continue;
      }

      // Update the corresponding arrays
      ideal[windowId] = idealValue;
      avg[windowId] = avgValue;
      window[windowId] = `Window ${windowId + 1}`;
    }

    windowResults = {
      upstream: {
        window: upstreamWindow,
        ideal: upstreamIdeal,
        avg: upstreamAvg
      },
      downstream: {
        window: downstreamWindow,
        ideal: downstreamIdeal,
        avg: downstreamAvg
      },
      mss: values[values.length - 1].value[mssIndex],
      rtt: parseFloat(values[values.length - 1].value[rttIndex]).toFixed(2),
      rttBase: values[values.length - 1].value[rttBaseIndex],
      mtu: values[values.length - 1].value[mtuIndex],
      engAIPAddr: engAIpIndex !== -1 ? values[values.length - 1].value[engAIpIndex] : '',
      engBPublicIPAddr:
        engBPubIPIndex !== -1 ? values[values.length - 1].value[engBPubIPIndex] : '',
      engBPrivateIPAddr:
        engBPriIPIndex !== -1 ? values[values.length - 1].value[engBPriIPIndex] : '',
      tcpPort: tcpPortIndex !== -1 ? values[values.length - 1].value[tcpPortIndex] : '',
      windowResultsReceived: windowResultsReceived
    };

    return windowResults;
  }

  //eslint-disable-next-line
  processResults(result: any, configuration: any): any {
    let windowResults = null;
    this.bulletChartOptions = {};
    const tableData = result['ietf-lmap-report:report'].result[0].table[0];
    const resultsArr = result['ietf-lmap-report:report'].result[0];
    const testTypeIndex = tableData.column.indexOf('ftp-type');
    const tableRowData = tableData.row[tableData.row.length - 1];
    windowResults = this.getWindowResults(result, configuration);
    this.upstreamWindowResult = windowResults.upstream;
    this.downstreamWindowResult = windowResults.downstream;
    const summary = this.getSummary(result, configuration);
    this.upstreamSummary = summary.upstreamSummary;
    this.downstreamSummary = summary.downstreamSummary;

    this.upstreamSummary['diagnosisMessage'] = this.getDiagnosis(
      'Upload',
      this.upstreamSummary['diagnosis']
    );
    this.downstreamSummary['diagnosisMessage'] = this.getDiagnosis(
      'Download',
      this.downstreamSummary['diagnosis']
    );
    this.testSummary['mtu'] = windowResults.mtu;
    this.testSummary['mss'] = windowResults.mss;
    //this.testSummary['rtt'] = parseFloat(windowResults.rtt).toFixed(2);
    this.testSummary['thinClientTcpPort'] = windowResults.tcpPort;
    this.testSummary['rttBase'] = parseFloat(windowResults.rttBase).toFixed(2);
    this.testSummary['httpsRttAvg'] = parseFloat(windowResults.httpsRttAvg).toFixed(2);
    this.testSummary['thinClientTcpPort'] = windowResults.tcpPort;
    this.testSummary['end'] = this.helperService.getReportDateTimeString(resultsArr.end);
    this.testSummary['start'] = this.helperService.getReportDateTimeString(resultsArr.start);

    this.testSummary['engAIPAddr'] = windowResults.engAIPAddr;
    this.testSummary['engBIPAddr'] = windowResults.engBIPAddr;
    this.testSummary['engBPublicIPAddr'] = windowResults.engBPublicIPAddr;
    this.testSummary['engBPrivateIPAddr'] = windowResults.engBPrivateIPAddr;
    this.testSummary['engBType'] = windowResults.engBType;
    this.testSummary['tcpPort'] = windowResults.tcpPort;

    //Not needed for customer view
    this.windowSummary = this.getWindowSummary(result);
    const testResults = this.getHttpTestResults();
    this.testResultsDataSource = new MatTableDataSource(testResults);

    const sanitizedUpstreamAvg = this.upstreamWindowResult.avg
      .filter(
        item => item !== 'empty' && item !== undefined && item !== null && !isNaN(Number(item))
      )
      .map(item => Number(item));

    const sanitizedDownstreamAvg = this.downstreamWindowResult.avg
      .filter(
        item => item !== 'empty' && item !== undefined && item !== null && !isNaN(Number(item))
      )
      .map(item => String(Number(item))); // Convert to string[] after sanitizing

    // Sanitize upstream window result
    const sanitizedUpstreamWindowResult = this.upstreamWindowResult.window
      ? this.upstreamWindowResult.window.filter(
          item => item !== 'empty' && item !== undefined && item !== null
        )
      : [];

    // Sanitize downstream window result
    const sanitizedDownstreamWindowResult = this.downstreamWindowResult.window
      ? this.downstreamWindowResult.window.filter(
          item => item !== 'empty' && item !== undefined && item !== null
        )
      : [];

    // Decide which sanitized data to use (upstream or downstream)
    const finalWindowResultData =
      sanitizedUpstreamWindowResult.length > 0
        ? sanitizedUpstreamWindowResult
        : sanitizedDownstreamWindowResult;

    if (
      this.upstreamSummary['verdict'] === 'FAIL' ||
      this.downstreamSummary['verdict'] === 'FAIL'
    ) {
      this.overallVerdict = 'FAIL';
    } else {
      this.overallVerdict = 'PASS';
    }
    // Call CreateBulletChart with sanitized data
    this.bulletChartOptions = this.transformService.CreateBulletChart(
      [], // Keep window labels as-is
      [configuration.engineAtoBThreshold], // Sanitized upstream ideal throughput (number[])
      [configuration.engineBtoAThreshold], // Sanitized downstream ideal throughput (string[])
      sanitizedUpstreamAvg, // Sanitized upstream average throughput (number[])
      sanitizedDownstreamAvg, // Sanitized downstream average throughput (string[])
      false, // runSaturation
      false, // dynamicSaturation
      this.testType, // Test type
      this.configuration['direction'] === 'bidirsequential'
        ? 'bidirsequential'
        : this.configuration['direction'] === 'engine-a-to-engine-b'
        ? 'Upload'
        : 'Download'
    );

    this.bulletChartOptions['series'].forEach(item => {
      const pointFormat = `<span style="color:${item.color}">\u25CF </span> ${item.name}:<b>{point.y}.
      </b> Threshold:<b>{point.target}</b>`;
      item['tooltip'] = { pointFormat: pointFormat };
    });
    this.loading = false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getWindowSummary(result): any {
    const windowResults = [];

    const table = result['ietf-lmap-report:report'].result[0].table[0];
    const originatorIndex = table.column.indexOf('originator');
    const windowIdIndex = table.column.indexOf('window-id');
    const windowSizeIndex = table.column.indexOf('window-size');
    const connectionIndex = table.column.indexOf('connections');
    const testTypeIndex = table.column.indexOf('truespeed-type');
    const dynamicSatConnectionIndex = table.column.indexOf('dynamic-saturation-total-connections');

    const column = table.column;
    const values = table.row;

    let upstreamIndex = 1;
    let downstreamIndex = 1;
    let windowResult = {};
    let columnIndex = 0;
    let columnName = null;
    let value = null;

    if (testTypeIndex === -1) {
      for (let i = 0; i < values.length; i++) {
        if (values[i].value[windowIdIndex] !== '') {
          windowResult = {};
          if (values[i].value[originatorIndex] === 'engine-a') {
            windowResult['direction'] = 'Upload';
            windowResult['index'] = upstreamIndex;
            upstreamIndex++;
          } else {
            windowResult['direction'] = 'Download';
            windowResult['index'] = downstreamIndex;

            downstreamIndex++;
          }

          for (columnIndex = 0; columnIndex < column.length; columnIndex++) {
            columnName = column[columnIndex];
            value = values[i].value[columnIndex];

            //Some formatting
            if (
              columnName === 'window-size' ||
              (columnName === 'dynamic-saturation-total-window-size' && value)
            ) {
              let connectionsValue = Number(values[i].value[connectionIndex]) || 0;
              value = Number(value) || 0;

              if (columnName === 'dynamic-saturation-total-window-size') {
                connectionsValue += Number(values[i].value[dynamicSatConnectionIndex]) || 0;
                value += Number(values[i].value[windowSizeIndex]) || 0;
                windowResult['dynamicSaturation'] = true;
              }
              let aggregateWindow = value * connectionsValue;
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              aggregateWindow = this.windowWalkService.formatWindowSize(aggregateWindow);
              windowResult['aggregate-window'] = aggregateWindow;

              value = this.windowWalkService.formatWindowSize(value);
              windowResult['connections'] = connectionsValue.toString();
              windowResult['window-size'] = value;
            } else if (
              columnName.indexOf('thruput') !== -1 ||
              columnName.indexOf('rate-under-test') !== -1 ||
              columnName.indexOf('buffer-delay') !== -1 ||
              columnName.indexOf('tcp-efficiency') !== -1
            ) {
              value = parseInt((Number(value) * 100).toString()) / 100;
            }

            windowResult[columnName] = value;
          }
          windowResults.push(windowResult);
        }
      }
    } else {
      for (let k = 1; k < values.length; k++) {
        windowResult = {};
        if (values[k].value[originatorIndex] === 'engine-a') {
          windowResult['direction'] = 'Upload';
          windowResult['index'] = upstreamIndex;
          windowResult['window-id'] = 0;
          upstreamIndex++;
        } else {
          windowResult['direction'] = 'Download';
          windowResult['index'] = downstreamIndex;
          windowResult['window-id'] = 1;
          downstreamIndex++;
        }

        for (columnIndex = 0; columnIndex < column.length; columnIndex++) {
          columnName = column[columnIndex];
          value = values[k].value[columnIndex];

          //Some formatting
          if (
            columnName.indexOf('avg-thruput') !== -1 ||
            columnName.indexOf('ideal-thruput') !== -1
          ) {
            value = parseFloat(value).toFixed(2);
          }

          windowResult[columnName] = value;
        }

        windowResults.push(windowResult);
      }
    }
    return windowResults;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getSummary(windowResults, configuration) {
    const upstreamSummary = { direction: 'up' };
    const downstreamSummary = { direction: 'down' };

    const table = windowResults['ietf-lmap-report:report'].result[0].table[0];
    const columns = table.column;
    const rows = table.row;

    // Get indexes of necessary columns
    const indices = {
      originator: columns.indexOf('originator'),
      idealThruput: columns.indexOf('ideal-thruput'),
      avgThruput: columns.indexOf('avg-thruput'),
      peakThruput: columns.indexOf('peak-thruput'),
      windowSize: columns.indexOf('window-size'),
      connections: columns.indexOf('connections'),
      engineAToBDiagnosis: columns.indexOf('engine-a-to-b-diagnosis'),
      engineBToADiagnosis: columns.indexOf('engine-b-to-a-diagnosis'),
      thruputThreshold: columns.indexOf('thruput-threshold'),
      targetThroughput: columns.indexOf('thruput-target')
    };

    let upstreamMaxAvg = 0;
    let downstreamMaxAvg = 0;
    let upstreamMaxPeak = 0;
    let downstreamMaxPeak = 0;

    rows.forEach(row => {
      const values = row.value;
      const originator = values[indices.originator];
      const avgThruput = parseFloat(values[indices.avgThruput]);
      const peakThruput = parseFloat(values[indices.peakThruput]);

      if (originator === 'engine-a') {
        // Upstream logic
        upstreamMaxAvg = Math.max(upstreamMaxAvg, avgThruput);
        upstreamMaxPeak = Math.max(upstreamMaxPeak, peakThruput);
        upstreamSummary['threshold'] = parseFloat(values[indices.thruputThreshold]);
        upstreamSummary['target'] = parseFloat(values[indices.targetThroughput]);
        upstreamSummary['idealThroughput'] = parseFloat(values[indices.idealThruput]);
      } else {
        // Downstream logic
        downstreamMaxAvg = Math.max(downstreamMaxAvg, avgThruput);
        downstreamMaxPeak = Math.max(downstreamMaxPeak, peakThruput);
        downstreamSummary['threshold'] = parseFloat(values[indices.thruputThreshold]);
        downstreamSummary['target'] = parseFloat(values[indices.targetThroughput]);
        downstreamSummary['idealThroughput'] = parseFloat(values[indices.idealThruput]);
      }
    });

    // Populate upstream summary
    upstreamSummary['bestAvg'] = upstreamMaxAvg.toFixed(2); //Math.min(upstreamMaxAvg, upstreamSummary['idealThroughput']);
    upstreamSummary['bestPeak'] = upstreamMaxPeak.toFixed(2); //Math.min(upstreamMaxPeak, upstreamSummary['idealThroughput']);
    upstreamSummary['verdict'] = upstreamMaxAvg < upstreamSummary['threshold'] ? 'FAIL' : 'PASS';
    upstreamSummary['diagnosis'] = rows[rows.length - 1].value[indices.engineBToADiagnosis];

    // Populate downstream summary
    downstreamSummary['bestAvg'] = downstreamMaxAvg.toFixed(2); //Math.min(downstreamMaxAvg, downstreamSummary['idealThroughput']);
    downstreamSummary['bestPeak'] = downstreamMaxPeak.toFixed(2);
    // Math.min(
    //   downstreamMaxPeak,
    //   downstreamSummary['idealThroughput']
    // );
    downstreamSummary['verdict'] =
      downstreamMaxAvg < downstreamSummary['threshold'] ? 'FAIL' : 'PASS';
    downstreamSummary['diagnosis'] = rows[rows.length - 1].value[indices.engineAToBDiagnosis];

    return { upstreamSummary, downstreamSummary };
  }

  sanitizeData(data: Array<number | string>): Array<number> {
    return data
      .filter(
        item => item !== 'empty' && item !== undefined && item !== null && !isNaN(Number(item))
      ) // Filter out invalid entries
      .map(item => Number(item)); // Convert valid entries to numbers
  }

  getDiagnosis(direction: string, diagnosis: string): Array<string> {
    let diagnosisList = [];
    if (diagnosis) {
      diagnosisList = diagnosis.split(',');
    }
    let diagnosisMap = this.upstreamDiagnosisMap;
    let messages = [];

    if (direction === 'Download') {
      diagnosisMap = this.downstreamDiagnosisMap;
    }

    for (let i = 0; i < diagnosisList.length; i++) {
      messages = messages.concat(diagnosisMap[diagnosisList[i]]);
    }

    return messages;
  }

  createDiagnosisMap(direction: string): { TCP_EFFICIENCY_LOW? } {
    const diagnosisMap = {};

    diagnosisMap['EFF_LOW_DELAY_LOW'] = [
      this.diagnosisMessage['SPECIAL1'],
      this.diagnosisMessage['SPECIAL3'],
      this.diagnosisMessage['CAUSE2'],
      this.diagnosisMessage['CAUSE3'],
      this.diagnosisMessage['CAUSE4'],
      this.diagnosisMessage['CAUSE5'],
      this.diagnosisMessage['CAUSE11'],
      this.diagnosisMessage['CAUSE1']
    ];

    diagnosisMap['EFF_LOW_DELAY_MED'] = [
      this.diagnosisMessage['SPECIAL1'],
      this.diagnosisMessage['SPECIAL3'],
      this.diagnosisMessage['CAUSE5'],
      this.diagnosisMessage['CAUSE2'],
      this.diagnosisMessage['CAUSE11'],
      this.diagnosisMessage['CAUSE3'],
      this.diagnosisMessage['CAUSE4'],
      this.diagnosisMessage['CAUSE1']
    ];

    diagnosisMap['EFF_HIGH_DELAY_LOW'] = [
      this.diagnosisMessage['SPECIAL1'],
      this.diagnosisMessage['SPECIAL5'],
      this.diagnosisMessage['CAUSE12'],
      this.diagnosisMessage['CAUSE13'],
      this.diagnosisMessage['CAUSE14']
    ];

    diagnosisMap['THRUPUT_LOW_MTU_SML'] = [
      this.diagnosisMessage['SPECIAL6'],
      this.diagnosisMessage['CAUSE10']
    ];

    diagnosisMap['POSSIBLE_PROXY_DETECTED'] = [this.diagnosisMessage['SPECIAL2']];

    diagnosisMap['NO_HIGH_ACCURACY_METRICS'] = [this.diagnosisMessage['SPECIAL8']];

    diagnosisMap['MTU_LESS_THAN_1400'] = [this.diagnosisMessage['SPECIAL7']];

    diagnosisMap['TCP_EFFICIENCY_LOW'] = [
      this.diagnosisMessage['SPECIAL3'],
      this.diagnosisMessage['CAUSE3'],
      this.diagnosisMessage['CAUSE4'],
      this.diagnosisMessage['CAUSE5'],
      this.diagnosisMessage['CAUSE2'],
      this.diagnosisMessage['CAUSE1']
    ];

    if (direction === 'Upload') {
      diagnosisMap['EFF_LOW_DELAY_HIGH'] = [
        this.diagnosisMessage['SPECIAL1'],
        this.diagnosisMessage['SPECIAL3'],
        this.diagnosisMessage['CAUSE5'],
        this.diagnosisMessage['CAUSE2'],
        this.diagnosisMessage['CAUSE3'],
        this.diagnosisMessage['CAUSE4'],
        this.diagnosisMessage['SPECIAL4'],
        this.diagnosisMessage['CAUSE6'],
        this.diagnosisMessage['CAUSE7_2'],
        this.diagnosisMessage['CAUSE8'],
        this.diagnosisMessage['CAUSE9'],
        this.diagnosisMessage['CAUSE11']
      ];

      diagnosisMap['EFF_HIGH_DELAY_HIGH'] = [
        this.diagnosisMessage['SPECIAL1'],
        this.diagnosisMessage['SPECIAL4'],
        this.diagnosisMessage['CAUSE6'],
        this.diagnosisMessage['CAUSE7_2'],
        this.diagnosisMessage['CAUSE8'],
        this.diagnosisMessage['CAUSE9'],
        this.diagnosisMessage['CAUSE11'],
        this.diagnosisMessage['CAUSE15']
      ];
    } else {
      diagnosisMap['EFF_LOW_DELAY_HIGH'] = [
        this.diagnosisMessage['SPECIAL1'],
        this.diagnosisMessage['SPECIAL3'],
        this.diagnosisMessage['CAUSE5'],
        this.diagnosisMessage['CAUSE2'],
        this.diagnosisMessage['CAUSE3'],
        this.diagnosisMessage['CAUSE4'],
        this.diagnosisMessage['SPECIAL4'],
        this.diagnosisMessage['CAUSE6'],
        this.diagnosisMessage['CAUSE7_1'],
        this.diagnosisMessage['CAUSE8'],
        this.diagnosisMessage['CAUSE9'],
        this.diagnosisMessage['CAUSE11']
      ];

      diagnosisMap['EFF_HIGH_DELAY_HIGH'] = [
        this.diagnosisMessage['SPECIAL1'],
        this.diagnosisMessage['SPECIAL4'],
        this.diagnosisMessage['CAUSE6'],
        this.diagnosisMessage['CAUSE7_1'],
        this.diagnosisMessage['CAUSE8'],
        this.diagnosisMessage['CAUSE9'],
        this.diagnosisMessage['CAUSE11'],
        this.diagnosisMessage['CAUSE15']
      ];
    }

    return diagnosisMap;
  }
  convertRate(rateType: string, rate: string): string {
    if (rateType && rate) {
      const mtu = parseFloat(this.testSummary['mtu']);
      const mss = parseFloat(this.testSummary['mss']);
      const rrr = parseFloat(rate);
      if (this.tagging === 'TAGGED') {
        this.l1Rate = (rrr * (mtu + 42)) / mss;
        this.l2Rate = this.l1Rate * ((mtu + 18) / (mtu + 42));
        this.l3Rate = this.l1Rate * (mtu / (mtu + 42));
      } else {
        this.l1Rate = (rrr * (mtu + 38)) / mss;
        this.l2Rate = this.l1Rate * ((mtu + 14) / (mtu + 38));
        this.l3Rate = this.l1Rate * (mtu / (mtu + 38));
      }

      switch (rateType) {
        case 'L1':
          return parseFloat(this.l1Rate).toFixed(2);
        case 'L2':
          return parseFloat(this.l2Rate).toFixed(2);
        case 'L3':
          return parseFloat(this.l3Rate).toFixed(2);
      }
    }
    return parseFloat(rate).toFixed(2);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && (changes.runId || changes.reportStartTime)) {
      this.requestInfo = {};
      this.upstreamSummary = {};
      this.downstreamSummary = {};
      this.testSummary = {};
      this.windowResults = [];
      this.windowSummary = [];
      this.configuration = {};
      this.upstreamWindowResult = null;
      this.downstreamWindowResult = null;
      this.dataAvailable = false;
      this.init();
    }
  }

  getHttpTestResults(): Array<TableData> {
    this.ftpTestResults = [];
    this.ftpTestResults.push({
      header: 'Engine A IP',
      value: this.testSummary['engAIPAddr'],
      hide: !this.testSummary['engAIPAddr']
    });
    this.ftpTestResults.push({
      header: this.testType === TestMode.CLIENT_TO_MA ? 'Client Public IP' : 'Engine B Public IP',
      value: this.testSummary['engBPublicIPAddr'],
      hide: !this.testSummary['engBPublicIPAddr']
    });
    if (this.testSummary['engBPrivateIPAddr']) {
      this.ftpTestResults.push({
        header:
          this.testType === TestMode.CLIENT_TO_MA ? 'Client Private IP' : 'Engine B Private IP',
        value: this.testSummary['engBPrivateIPAddr'],
        hide: !this.testSummary['engBPrivateIPAddr']
      });
    }
    if (this.testSummary['tcpPort']) {
      this.ftpTestResults.push({
        header: 'TCP Port',
        value: this.testSummary['tcpPort']
      });
    }
    this.ftpTestResults.push({
      header: 'RTT Base',
      value: this.testSummary['rttBase'] + ' ms'
    });

    if (
      this.configuration['direction'] === 'engine-a-to-engine-b' ||
      this.configuration['direction'] === 'bidirsequential'
    ) {
      this.ftpTestResults.push(
        {
          header: 'Max Upload Avg Throughput',
          value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(this.upstreamSummary['bestAvg'])}
        </table>`
        },
        {
          header: 'Max Upload Peak Throughput',
          value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(this.upstreamSummary['bestPeak'])}
        </table>`
        }
      );
    }
    if (
      this.configuration['direction'] === 'engine-b-to-engine-a' ||
      this.configuration['direction'] === 'bidirsequential'
    ) {
      this.ftpTestResults.push(
        {
          header: 'Max Download Avg Throughput',
          value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(this.downstreamSummary['bestAvg'])}
        </table>`
        },
        {
          header: 'Max Download Peak Throughput',
          value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(this.downstreamSummary['bestPeak'])}
        </table>`
        }
      );
    }
    return this.ftpTestResults;
  }
  //eslint-disable-next-line
  getwindowResultDataSource(windowResult: any): MatTableDataSource<TableData> {
    const configuration = [
      {
        header: 'Window Size Per Connection',
        value: windowResult['window-size'],
        hide: windowResult?.['dynamicSaturation'] === true
      },
      {
        header: 'Connections',
        value: windowResult['connections']
      },
      {
        header: 'Aggregate Window',
        value: windowResult['aggregate-window']
      },
      {
        header: 'Actual Throughput',
        value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(windowResult['avg-thruput'])}
        </table>`
      },
      {
        header: 'Ideal Throughput',
        value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(windowResult['ideal-thruput'])}
        </table>`
      },
      {
        header: 'Target Throughput',
        value: `
        <table class="throughput-inner-table">
        ${this.throughputInnerTableHeader}
        ${this.getThroughputConvertedData(windowResult?.['ideal-thruput'])}
        </table>`
      },
      {
        header: 'Total Retransmits',
        value: this.getRetransmit(windowResult)
      }
    ];
    return new MatTableDataSource(configuration);
  }
  // eslint-disable-next-line
  getRetransmit(windowResult: any): any {
    if (!windowResult['total-retransmit']) {
      this.calculateTotalStat();
    }
    if (windowResult?.['total-retransmit'] >= 0) {
      return windowResult?.['total-retransmit'];
    } else {
      return 'Data no longer available';
    }
  }

  getThroughputConvertedData(rate: string): string {
    return `<tr>
    <td class="throughput-inner-table-data">${this.convertRate('L1', rate)} Mbps </td>
    <td class="throughput-inner-table-data">${this.convertRate('L2', rate)} Mbps </td>
    <td class="throughput-inner-table-data">${this.convertRate('L3', rate)} Mbps</td>
    <td class="throughput-inner-table-data">${this.convertRate('L4', rate)} Mbps</td>
    </tr>`;
  }

  calculateTotalStat(): void {
    for (let windowCount = 0; windowCount < this.windowResults.length; windowCount++) {
      const windowResult = this.windowResults[windowCount];
      let totalTranmit = 0;
      let totalRetransmit = 0;
      for (let j = 0; j < windowResult?.transmitted.length; j++) {
        totalTranmit += windowResult.transmitted[j];
        totalRetransmit += windowResult.retransmitted[j];
      }

      for (let k = 0; k < this.windowSummary.length; k++) {
        if (this.windowSummary[k]['window-id'] === windowResult?.id) {
          this.windowSummary[k]['total-transmit'] = totalTranmit;
          this.windowSummary[k]['total-retransmit'] = totalRetransmit;
        }
      }
    }
  }

  ngAfterViewInit() {
    this.dataAvailable = true;
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
